// WiselyDriven Design Tokens
// Centralized theme configuration - change values here to restyle the entire site
// Only includes values actually used in the application

module.exports = {
  // Colors
  colors: {
    // Main backgrounds
    main: {
      light: '#F9FAFB',   // Main page background (light theme)
      dark: '#111827',    // Main page background (dark theme)
    },

    // Card backgrounds
    card: {
      light: '#E5E7EB',   // Card backgrounds, modals (light theme)
      dark: '#1F2937',    // Card backgrounds, modals (dark theme)
    },

    // Secondary card/surface backgrounds
    surface: {
      light: '#D1D5DB',   // Secondary backgrounds, setup/login pages (light theme)
      dark: '#374151',    // Input fields, secondary cards (dark theme)
    },

    // Primary action button
    action: {
      light: '#F97316',   // Primary buttons (light theme)
      dark: '#F97316',    // Primary buttons (dark theme)
    },

    // Primary action button hover
    actionHover: {
      light: '#EA6C0A',   // Button hover states (light theme)
      dark: '#FB923C',    // Button hover states (dark theme)
    },

    // Primary text
    textPrimary: {
      light: '#111827',   // Headings (light theme)
      dark: '#F9FAFB',    // Headings, primary text (dark theme)
    },

    // Secondary text
    textSecondary: {
      light: '#374151',   // Body text, buttons (light theme)
      dark: '#E5E7EB',    // Body text, buttons (dark theme)
    },

    // Muted text
    textMuted: {
      light: '#4B5563',   // Secondary text, descriptions (light theme)
      dark: '#D1D5DB',    // Secondary text, descriptions (dark theme)
    },

    // Very muted text
    textMutedSubtle: {
      light: '#6B7280',   // Muted text, placeholders (light theme)
      dark: '#6B7280',    // Muted text (dark theme)
    },

    // Borders
    border: {
      light: '#9CA3AF',   // Default borders (light theme)
      dark: '#374151',    // Default borders (dark theme)
    },

    // Borders (subtle)
    borderSubtle: {
      light: '#D1D5DB',   // Subtle borders (light theme)
      dark: '#4B5563',    // Subtle borders (dark theme)
    },

    // Success
    success: {
      light: '#059669',   // Success buttons (light theme)
      dark: '#10B981',    // Success buttons (dark theme)
    },

    successHover: {
      light: '#047857',   // Success hover states (light theme)
      dark: '#059669',    // Success hover states (dark theme)
    },

    successBg: {
      light: '#ECFDF5',                // Success backgrounds (light theme)
      dark: `rgba(6, 78, 59, 0.3)`,   // emerald-900 with opacity (dark theme)
    },

    successText: {
      light: '#10B981',   // Success text, icons (light theme)
      dark: '#34D399',    // Success icons (dark theme)
    },

    successBorder: {
      light: '#A7F3D0',   // Success borders (light theme)
      dark: '#065F46',    // Success borders (dark theme)
    },

    // Error/Destructive
    error: {
      light: '#E11D48',   // Error buttons, destructive actions (light theme)
      dark: '#F43F5E',    // Error buttons (dark theme)
    },

    errorHover: {
      light: '#BE123C',   // Error hover states (light theme)
      dark: '#E11D48',    // Error hover states (dark theme)
    },

    errorBg: {
      light: '#FFF1F2',                 // Error backgrounds (light theme)
      dark: `rgba(136, 19, 55, 0.3)`,  // rose-900 with opacity (dark theme)
    },

    errorText: {
      light: '#F43F5E',   // Error text, icons (light theme)
      dark: '#FB7185',    // Error icons (dark theme)
    },

    errorBorder: {
      light: '#FECDD3',   // Error borders (light theme)
      dark: '#9F1239',    // Error borders (dark theme)
    },

    // Info
    info: {
      light: '#F97316',   // Info buttons (light theme)
      dark: '#F97316',    // Info buttons (dark theme)
    },

    infoHover: {
      light: '#EA6C0A',   // Info hover states (light theme)
      dark: '#FB923C',    // Info hover states (dark theme)
    },

    infoBg: {
      light: '#FFF7ED',                  // Info backgrounds (light theme)
      dark: `rgba(154, 52, 18, 0.3)`,   // orange-900 with opacity (dark theme)
    },

    infoText: {
      light: '#F97316',   // Info text, icons (light theme)
      dark: '#FB923C',    // Info icons (dark theme)
    },

    infoBorder: {
      light: '#FED7AA',   // Info borders (light theme)
      dark: '#7C2D12',    // Info borders (dark theme)
    },

    // Link colors
    link: {
      light: '#F97316',   // Link text (light theme)
      dark: '#FB923C',    // Link text (dark theme)
    },

    linkBg: {
      light: '#FFF7ED',                  // Link backgrounds (light theme)
      dark: `rgba(249, 115, 22, 0.3)`,  // orange-500 with opacity (dark theme)
    },

    linkBorder: {
      light: '#FED7AA',   // Link borders (light theme)
      dark: '#7C2D12',    // Link borders (dark theme)
    },

    linkHover: {
      light: '#FED7AA',   // Link hover backgrounds (light theme)
      dark: '#7C2D12',    // Link hover (dark theme)
    },

    // Input fields
    inputBg: {
      light: '#FFFFFF',   // Input backgrounds (light theme)
      dark: '#374151',    // Input backgrounds (dark theme)
    },

    inputBorder: {
      light: '#E5E7EB',   // Input borders (light theme)
      dark: '#374151',    // Input borders (dark theme)
    },

    inputText: {
      light: '#111827',   // Input text (light theme)
      dark: '#F3F4F6',    // Input text (dark theme)
    },

    // Focus ring
    focusRing: {
      light: '#F97316',   // Focus ring (light theme)
      dark: '#F97316',    // Focus ring (dark theme)
    },

    // Modal overlay
    overlay: {
      light: `rgba(0, 0, 0, 0.6)`,   // Modal overlay (light theme)
      dark: `rgba(0, 0, 0, 0.8)`,    // Modal overlay (dark theme)
    },

    // Confirm/Neutral button
    confirm: {
      light: '#F97316',   // Confirm button (light theme)
      dark: '#F97316',    // Confirm button (dark theme)
    },

    confirmHover: {
      light: '#EA6C0A',   // Confirm hover (light theme)
      dark: '#FB923C',    // Confirm hover (dark theme)
    },

    confirmText: {
      light: '#FFFFFF',   // Confirm text (light theme)
      dark: '#FFFFFF',    // Confirm text (dark theme)
    },

    confirmBg: {
      light: '#FFF7ED',   // Confirm backgrounds (light theme)
      dark: '#1F2937',    // Confirm backgrounds (dark theme)
    },

    confirmBorder: {
      light: '#FED7AA',   // Confirm borders (light theme)
      dark: '#374151',    // Confirm borders (dark theme)
    },
  },

  // Typography
  fonts: {
    heading: "'DM Serif Display', Georgia, serif",
    body: "'Outfit', system-ui, sans-serif",
  },

  // Border Radius (corner radius)
  // Used for: rounded-card, rounded-container, rounded-button, rounded-input, rounded-badge, rounded-full
  borderRadius: {
    'card': '40px',        // Cards, modals (equivalent to rounded-2xl)
    'container': '25px',   // Simple containers, info boxes (equivalent to rounded-lg)
    'page': '1.5rem',      // Setup/login pages (equivalent to rounded-3xl)
    'button': '9999px',    // Buttons (equivalent to rounded-full)
    'input': '0.75rem',    // Input fields (equivalent to rounded-xl)
    'badge': '6px',        // Badges, labels like "owner", "member" (small rounded corners)
    'full': '9999px',      // Full circle (equivalent to rounded-full)
  },

  // Border Width (thickness)
  // Used for: border-default, border-thick, border-avatar, border-device
  borderWidth: {
    'default': '1px',   // Default borders (standard border)
    'thick': '2px',     // Thick borders (border-2)
    'avatar': '6px',    // Avatar borders (border-[6px])
    'device': '8px',    // Device preview borders (border-8)
  },

  // Background Textures
  // Set to empty string ('') to disable texture for that background
  // Paths are relative to public folder
  textures: {
    main: {
      light: '',   // Main background texture (light theme) - empty = no texture
      dark: '',    // Main background texture (dark theme) - empty = no texture
      size: '',
      blendLight: '',
      blendDark: '',
      opacityLight: 0,
      opacityDark: 0,
    },
    surface: {
      light: '',   // Surface background texture (light theme) - empty = no texture
      dark: '',    // Surface background texture (dark theme) - empty = no texture
    },
    card: {
      light: '',   // Card background texture (light theme) - empty = no texture
      dark: '',    // Card background texture (dark theme) - empty = no texture
    },
  },
};
